import { useEffect, useMemo } from 'react';

import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import { useQuery } from 'react-query';
import { getFidcComparatorIntersectedPeriods } from 'src/services/fidcComparator';

import { formatBaseMonthLabel, isValidBaseMonth } from '../dateUtils';

interface FidcComparisonSelectorProps {
  operationIds: Array<number | string>;
  baseMonth: string;
  onBaseMonthChange: (baseMonth: string) => void;
}

export default function FidcComparisonSelector({
  operationIds,
  baseMonth,
  onBaseMonthChange,
}: FidcComparisonSelectorProps) {
  const operationIdsKey = operationIds.map(String).join(',');
  const { data, isLoading, isError } = useQuery(
    ['fidc-comparator-intersected-periods', operationIdsKey],
    () => getFidcComparatorIntersectedPeriods(operationIds),
    {
      enabled: operationIds.length > 0,
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  const baseMonthOptions = useMemo(
    () =>
      Array.from(new Set(data ?? []))
        .filter(isValidBaseMonth)
        .sort((first, second) => second.localeCompare(first))
        .map((value) => ({ value, label: formatBaseMonthLabel(value) })),
    [data],
  );

  useEffect(() => {
    if (isLoading || isError || !baseMonthOptions.length) return;
    if (baseMonthOptions.some((option) => option.value === baseMonth)) return;

    onBaseMonthChange(baseMonthOptions[0].value);
  }, [baseMonth, baseMonthOptions, isError, isLoading, onBaseMonthChange]);

  const hasOptions = baseMonthOptions.length > 0;
  const selectedOptionExists = baseMonthOptions.some(
    (option) => option.value === baseMonth,
  );
  const selectedValue = hasOptions && selectedOptionExists ? baseMonth : '';
  const helperText = isLoading
    ? 'Carregando competências...'
    : isError
      ? 'Erro ao carregar competências.'
      : hasOptions
        ? 'Período de competência da comparação'
        : 'Nenhuma competência disponível para os FIDCs selecionados.';

  return (
    <FormControl
      size="small"
      disabled={isLoading || !hasOptions}
      error={isError}
      sx={{
        width: 'fit-content',
        minWidth: { xs: 180, sm: 190 },
        maxWidth: '100%',
      }}>
      <InputLabel id="fidc-comparison-base-month-label">Competência</InputLabel>
      <Select<string>
        labelId="fidc-comparison-base-month-label"
        id="fidc-comparison-base-month"
        label="Competência"
        value={selectedValue}
        onChange={(event) => onBaseMonthChange(event.target.value)}
        renderValue={(value) => formatBaseMonthLabel(value)}>
        {baseMonthOptions.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
      <FormHelperText>{helperText}</FormHelperText>
    </FormControl>
  );
}
